import * as React from "react";
import { cn } from "@/lib/utils";

/** Two-letter initials from a name (e.g. "Maria Lopez" → "ML"). */
export function initials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/**
 * Initials avatar for a chef/user. Unifies OrderPrimitives.ChefAvatar and the
 * inline initials badges in the cooking views.
 */
const sizeClass = {
  sm: "size-[1.875rem] text-[0.5625rem]",
  md: "size-9 text-xs",
} as const;

const shapeClass = {
  circle: "rounded-full",
  rounded: "rounded-lg",
} as const;

export interface ChefAvatarProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  name?: string | null;
  size?: keyof typeof sizeClass;
  shape?: keyof typeof shapeClass;
}

export function ChefAvatar({
  name,
  size = "sm",
  shape = "circle",
  className,
  ...props
}: ChefAvatarProps) {
  if (!name) {
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center border border-dashed border-(--color-border) bg-(--color-surface-muted) font-bold text-(--color-text-secondary) shrink-0",
          sizeClass[size],
          shapeClass[shape],
          className,
        )}
        {...props}
      >
        ?
      </span>
    );
  }
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center bg-(--brand-blue)/15 font-bold text-(--brand-blue) shrink-0",
        sizeClass[size],
        shapeClass[shape],
        className,
      )}
      {...props}
    >
      {initials(name)}
    </span>
  );
}
